export function createStylesheetLink(
    id: string,
    href: string,
    type = 'text/css'
): HTMLLinkElement {
    const newLink = document.createElement('link');
    newLink.id = id;
    newLink.rel = 'stylesheet';
    newLink.type = type;
    newLink.href = href;
    return newLink;
}

export function createStylesheetTag(
    id: string,
    content = ''
): HTMLStyleElement {
    const style = document.createElement('style');
    style.id = id;
    style.textContent = content;
    return style;
}

export function addStylesheetLink(
    id: string,
    href: string,
    type = 'text/css',
    target?: HTMLElement
): Promise<void> {
    const newLink = createStylesheetLink(id, href, type);
    target ??= document.head;
    target.appendChild(newLink);
    return new Promise((resolve) => {
        newLink.addEventListener('load', () => resolve());
    });
}

export function addStylesheetTag(
    id: string,
    content = '',
    target?: HTMLElement
) {
    const tag = createStylesheetTag(id, content);
    target ??= document.head;
    target.appendChild(tag);
    return tag;
}

export function getStylesheet(
    el: string | HTMLLinkElement | HTMLStyleElement
): HTMLLinkElement | HTMLStyleElement | null {
    if (typeof el === 'string') {
        return document.getElementById(el) as
            | HTMLLinkElement
            | HTMLStyleElement
            | null;
    }
    return el;
}

export function deleteStylesheet(
    linkOrId: string | HTMLLinkElement | HTMLStyleElement
) {
    const link = getStylesheet(linkOrId);
    if (link && link.parentNode) {
        link.parentNode.removeChild(link);
    }
}

/**
 * Switches the href of a stylesheet link without screen flashes, as it will wait till the new
 * href is loaded.
 */
function setLinkHref(
    linkOrId: string | HTMLLinkElement,
    href: string
): Promise<void> {
    const link = getStylesheet(linkOrId);
    if (!link) throw new Error('Link not found');
    if (!link.parentNode) throw Error('Link has no parent node');

    const id = link.getAttribute('id');

    const clonedLink = link.cloneNode(true) as HTMLLinkElement;
    clonedLink.setAttribute('href', href);
    if (id) clonedLink.setAttribute('id', `${id}-clone`);
    link.parentNode.insertBefore(clonedLink, link.nextSibling);

    return new Promise((resolve) => {
        clonedLink.addEventListener('load', () => {
            link.remove();
            if (id) clonedLink.setAttribute('id', id);
            else clonedLink.removeAttribute('id');
            resolve();
        });
    });
}

export async function setStylesheetLink(
    id: string,
    href: string | null,
    type = 'text/css',
    target?: HTMLElement
): Promise<void> {
    const link = getStylesheet(id);
    if (href === null) {
        if (link) deleteStylesheet(link);
    } else if (link instanceof HTMLLinkElement) {
        return setLinkHref(link, href);
    } else if (link instanceof HTMLStyleElement) {
        const result = await addStylesheetLink(id, href, type, target);
        deleteStylesheet(link);
        return result;
    } else {
        return addStylesheetLink(id, href, type, target);
    }
}

export function setStylesheetTag(
    id: string,
    content: string | null,
    target?: HTMLElement
) {
    const el = getStylesheet(id);
    if (content === null) {
        deleteStylesheet(id);
    } else if (el instanceof HTMLLinkElement) {
        const result = addStylesheetTag(id, content, target);
        deleteStylesheet(el);
        return result;
    } else if (el instanceof HTMLStyleElement) {
        el.textContent = content;
    } else {
        return addStylesheetTag(id, content, target);
    }
    return undefined;
}
